// src/lib/api/services/categories.js
import { supabase } from '../../supabase'
import { getValidatedCompanyId, getValidatedUserId } from '../shared/helpers'
import { withErrorHandling, createSuccessResponse } from '../shared/errors'
import { validateRequired, sanitizeInput } from '../shared/validation'

/**
 * Get all service categories
 * Migrated from supabaseApi.js lines 2582-2595
 * @param {string} [providedCompanyId] - Optional company ID (avoids internal lookup)
 */
const _getServiceCategories = async (providedCompanyId = null) => {
  const companyId = providedCompanyId || await getValidatedCompanyId()
  if (!companyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('service_categories')
    .select('*')
    .eq('company_id', companyId)
    .eq('is_active', true)
    .order('display_order', { ascending: true })
  
  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Create a new service category
 * Migrated from supabaseApi.js lines 2597-2615
 */
const _createServiceCategory = async (categoryData) => {
  validateRequired(categoryData.name, 'Category name')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const userId = await getValidatedUserId()
  if (!userId) {
    throw new Error('User not authenticated')
  }

  const sanitizedData = {
    ...categoryData,
    name: sanitizeInput(categoryData.name),
    description: categoryData.description ? sanitizeInput(categoryData.description) : null,
    company_id: companyId,
    created_by: userId
  }
  
  const { data, error } = await supabase
    .from('service_categories')
    .insert([sanitizedData])
    .select()
    .single()
  
  if (error) throw error
  return data
}

/**
 * Update an existing service category
 * Migrated from supabaseApi.js lines 2617-2635
 */
const _updateServiceCategory = async (categoryId, categoryData) => {
  validateRequired(categoryId, 'Category ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const sanitizedData = {
    ...categoryData,
    company_id: companyId
  }
  
  if (sanitizedData.name) {
    sanitizedData.name = sanitizeInput(sanitizedData.name)
  }
  if (sanitizedData.description) {
    sanitizedData.description = sanitizeInput(sanitizedData.description)
  }
  
  const { data, error } = await supabase
    .from('service_categories')
    .update(sanitizedData)
    .eq('id', categoryId)
    .select()
    .single()
  
  if (error) throw error
  return data
}

/**
 * Delete a service category
 * Migrated from supabaseApi.js lines 2637-2650
 */
const _deleteServiceCategory = async (categoryId) => {
  validateRequired(categoryId, 'Category ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { error } = await supabase
    .from('service_categories')
    .delete()
    .eq('id', categoryId)
  
  if (error) throw error
  return { success: true }
}

/**
 * Get service category by ID
 */
const _getServiceCategoryById = async (categoryId) => {
  validateRequired(categoryId, 'Category ID')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { data, error } = await supabase
    .from('service_categories')
    .select('*')
    .eq('id', categoryId)
    .eq('company_id', companyId)
    .single()
  
  if (error) throw error
  return data
}

/**
 * Get services by category
 */
const _getServicesByCategory = async (categoryId, companyId = null) => {
  validateRequired(categoryId, 'Category ID')
  
  const resolvedCompanyId = companyId || await getValidatedCompanyId()
  if (!resolvedCompanyId) {
    return createSuccessResponse([])
  }

  const { data, error } = await supabase
    .from('services')
    .select(`
      *,
      service_categories(name)
    `)
    .eq('company_id', resolvedCompanyId)
    .eq('category_id', categoryId)
    .order('sort_order', { ascending: true })
  
  if (error) throw error
  return createSuccessResponse(data || [])
}

/**
 * Update category display order
 */
const _updateCategoryDisplayOrder = async (categoryId, displayOrder) => {
  validateRequired(categoryId, 'Category ID')
  validateRequired(displayOrder, 'Display order')
  
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }
  
  const { data, error } = await supabase
    .from('service_categories')
    .update({ 
      display_order: displayOrder,
      updated_at: new Date().toISOString()
    })
    .eq('id', categoryId)
    .eq('company_id', companyId)
    .select()
    .single()
  
  if (error) throw error
  return data
}

// Export wrapped functions with error handling
/**
 * Get or create the "Price assisted" category
 * Auto-creates the category if it doesn't exist
 */
const _getOrCreatePriceAssistedCategory = async () => {
  const companyId = await getValidatedCompanyId()
  if (!companyId) {
    throw new Error('No company ID found')
  }

  // First, try to find existing category
  const { data: existing, error: fetchError } = await supabase
    .from('service_categories')
    .select('*')
    .eq('company_id', companyId)
    .eq('name', 'Price assisted')
    .eq('is_active', true)
    .maybeSingle()

  if (fetchError) throw fetchError

  // If category exists, return it
  if (existing) {
    return existing
  }

  // Category doesn't exist, create it
  // Get max display_order to append at the end
  const { data: maxOrderData } = await supabase
    .from('service_categories')
    .select('display_order')
    .eq('company_id', companyId)
    .order('display_order', { ascending: false })
    .limit(1)
    .maybeSingle()

  const maxOrder = maxOrderData?.display_order ?? 0
  const newDisplayOrder = maxOrder + 1

  const userId = await getValidatedUserId()
  if (!userId) {
    throw new Error('User not authenticated')
  }

  const categoryData = {
    name: 'Price assisted',
    description: 'Services created with AI price assistance',
    company_id: companyId,
    created_by: userId,
    display_order: newDisplayOrder,
    is_active: true
  }

  const { data: newCategory, error: createError } = await supabase
    .from('service_categories')
    .insert([categoryData])
    .select()
    .single()

  if (createError) throw createError
  return newCategory
}

export const getServiceCategories = withErrorHandling(_getServiceCategories, 'fetch service categories', 'service_categories')
export const createServiceCategory = withErrorHandling(_createServiceCategory, 'create service category', 'service_category')
export const updateServiceCategory = withErrorHandling(_updateServiceCategory, 'update service category', 'service_category')
export const deleteServiceCategory = withErrorHandling(_deleteServiceCategory, 'delete service category', 'service_category')
export const getServiceCategoryById = withErrorHandling(_getServiceCategoryById, 'get service category by ID', 'service_category')
export const getServicesByCategory = withErrorHandling(_getServicesByCategory, 'get services by category', 'services')
export const updateCategoryDisplayOrder = withErrorHandling(_updateCategoryDisplayOrder, 'update category display order', 'service_category')
export const getOrCreatePriceAssistedCategory = withErrorHandling(_getOrCreatePriceAssistedCategory, 'get or create Price assisted category', 'service_category')